import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe, getAdminKey } from '../lib/api';

const AuthContext = createContext(null);

// Fetch + cache X-Admin-Key when the logged-in user is admin, so admin UI
// (Publicaciones / AdminReels / AdminPublisher / AdminMusic) can call the
// X-Admin-Key-protected endpoints without a manual paste.
async function bootstrapAdminKey(user) {
  try {
    if (!user?.is_admin) return;
    const existing = localStorage.getItem('admin_key');
    if (existing) return;
    const res = await getAdminKey();
    if (res?.data?.admin_key) {
      localStorage.setItem('admin_key', res.data.admin_key);
    }
  } catch {
    // silent — admin key is optional for non-admin pages
  }
}

export const AuthProvider = ({ children }) => {
  // Initialize user from localStorage for instant UI render
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    // If we have a cached user, don't show loading
    const savedUser = localStorage.getItem('user');
    return !savedUser;
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Validate token in background, don't block UI
      getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
          bootstrapAdminKey(res.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('admin_key');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const loginUser = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    bootstrapAdminKey(userData);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('admin_key');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
